import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import ProjectDisclaimer from "@/components/ProjectDisclaimer";

const Privacy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | AILens</title>
        <meta name="description" content="AILens Privacy Policy: We do not store your scan data unless you explicitly request a report. Learn how we protect your brand's search information." />
        <meta property="og:title" content="Privacy Policy | AILens" />
        <meta property="og:description" content="AILens Privacy Policy: We do not store your scan data unless you explicitly request a report." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ai-lens.app/privacy" />
        <link rel="canonical" href="https://ai-lens.app/privacy" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Privacy Policy | AILens" />
        <meta name="twitter:description" content="AILens Privacy Policy: How we protect your data." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <div className="container max-w-3xl mx-auto px-4 py-12">
          
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Scanner
          </Link>

          <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground mb-8">Last updated: February 2026</p>

          <div className="prose prose-gray max-w-none">

            <h2 className="text-xl font-semibold mt-8 mb-4">Information We Collect</h2>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>Brand names and keywords: Data you submit for scanning.</li>
              <li>Email addresses: Collected if you choose to get in touch via the contact email.</li>
              <li>Usage analytics: Anonymous data on how you use the site.</li>
              <li>Website technical data: When you use the Technical AI Checks feature, we fetch publicly available files (robots.txt, sitemap.xml, llms.txt, HTML). This data is analyzed in real-time and not stored permanently.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">How We Use Information</h2>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>To provide AI visibility scanning services.</li>
              <li>To respond to inquiries from users who reach out.</li>
              <li>To improve our service based on anonymous feedback.</li>
              <li>To perform real-time technical analysis of websites you submit.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">Data Retention</h2>
            <p className="mb-6">
              Scan results and technical checks data are processed in real-time and not permanently stored. Email addresses are retained until you request deletion. Rate limiting data (IP addresses) is automatically deleted after 2 hours.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">Data Storage</h2>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>Scan results are not permanently stored.</li>
              <li>Email correspondence is handled through our standard email provider (Outlook). No email addresses are collected or stored within the application itself.</li>
              <li>We use secure cloud infrastructure for data processing.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">Third-Party Services</h2>
            
            <p className="mb-4">
              Our service relies on third-party providers for core functionality. Service interruptions or changes by these providers may temporarily affect scan availability or accuracy. We are not responsible for disruptions caused by third-party changes.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">AI Analysis</h3>
            <p className="mb-4">
              We send the brand names and keywords you submit to OpenAI's API for AI visibility analysis. OpenAI processes this data according to their{' '}
              <a href="https://openai.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">privacy policy</a>.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">Search Data</h3>
            <p className="mb-4">
              We use third-party search data providers to retrieve public ranking information for the brands and keywords you submit.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">Analytics & Performance</h3>
            <p className="mb-4">
              We use analytics and performance monitoring tools to understand how visitors interact with the application, identify technical issues, and improve the user experience. These tools may collect anonymous usage data such as pages visited, time on site, and general behavior patterns. No personally identifiable information is shared with analytics providers.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">Infrastructure</h3>
            <p className="mb-6">
              We use secure cloud infrastructure providers for database hosting and email delivery.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">Technical AI Checks</h2>
            <p className="mb-4">
              When you use the Technical AI Checks feature, the system checks publicly available files on the domain you provided to assess AI crawler accessibility. This includes checking robots.txt directives, sitemap availability, llms.txt presence, and Schema markup.
            </p>
            <p className="mb-4">
              This analysis is performed in real-time and the fetched data is not stored on our servers. Only the pass/fail results are displayed to you during your session. We do not modify, cache, or redistribute any content fetched from your website.
            </p>
            <p className="mb-6">
              <strong>Important limitations:</strong> This check only detects server-rendered content. Schema markup injected via JavaScript (such as through Google Tag Manager) may not be detected. Results are best-effort and should not be treated as a definitive technical audit.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">Cookies</h2>
            <p className="mb-6">
              We use essential cookies for site functionality and analytics cookies to improve our service. You can manage cookie preferences through your browser settings.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">Service Status</h2>
            <p className="mb-6">
              AILens is an experimental research project provided as-is.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">International Users</h2>
            <p className="mb-6">
              AILens processes data through secure cloud infrastructure. By using our service, you consent to the processing of your information as described in this policy. We comply with applicable data protection laws including GDPR principles.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">Your Rights</h2>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>Request information about what data, if any, is associated with your use</li>
              <li>Request deletion of any correspondence by contacting us at <a href="mailto:ai-lens@outlook.com" className="text-primary hover:underline">ai-lens@outlook.com</a></li>
              <li>Opt-out of analytics via browser settings</li>
              <li>Withdraw consent at any time</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">Data Security</h2>
            <p className="mb-6">
              We implement industry-standard security measures to protect your data, including encrypted connections (HTTPS), secure cloud infrastructure, and limited access controls. However, no method of transmission over the Internet is 100% secure.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">Children's Privacy</h2>
            <p className="mb-6">
              AILens is not intended for users under 16 years of age. We do not knowingly collect personal information from children.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">Changes to This Policy</h2>
            <p className="mb-6">
              We may update this privacy policy from time to time. Changes will be reflected by updating the "Last updated" date.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">Contact</h2>
            <p className="mb-6">For any questions or concerns about this privacy policy, contact us at <a href="mailto:ai-lens@outlook.com" className="text-primary hover:underline">ai-lens@outlook.com</a>.</p>

          </div>

          <ProjectDisclaimer />
        </div>
      </div>
    </>
  );
};

export default Privacy;
